import { makeStyles, Typography } from 'beam'
import { convertForBackend } from 'c1-i18n'
import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { AuthLayout } from '../../Common/AuthLayout'
import { NguiSnackbar } from '../../Common/NguiSnackbar'
import { isAxiosError } from '../../Helpers/axiosTypes'
import { isC1ApiError } from '../../Helpers/c1ApiTypes'
import { User } from '../../Permissions/AuthenticatedUser.context'
import { RootState } from '../app.reducer'
import { ChooseIdpMethod } from './ChooseIdpMethod'
import { ChooseLoginMethod, LoginMethodChoice } from './ChooseLoginMethod'
import { EmailEntry } from './EmailEntry'
import { ForgotPasswordDialog } from './ForgotPasswordDialog'
import { LoggingIn } from './LoggingIn'
import { getLoginMethod, login, redirectToForgeRockLogin, singleSignOn, TenantSummary } from './loginApi'
import { PasswordEntry } from './PasswordEntry'
import { TenantResolution } from './TenantResolution'

const useStyles = makeStyles(theme => ({
  title: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  body: {
    marginBottom: theme.spacing(2),
  },
}))

interface EmailEntryState {
  type: 'enterEmail',
  lastSubmittedEmail?: string,
}

interface PasswordEntryState {
  type: 'enterPassword',
  email: string,
  showForgotPasswordDialog?: boolean,
}

interface C1TenantResolutionState {
  type: 'resolveTenant',
  authMethod: 'c1',
  tenants: TenantSummary[],
  email: string,
  password: string,
}

interface SSOTenantResolutionState {
  type: 'resolveTenant',
  authMethod: 'sso',
  tenants: TenantSummary[],
  email: string,
}

// This is the "isLoading" status
interface LoggingInState {
  type: 'loggingIn',
  email: string,
  tenant?: TenantSummary,
}

interface ChooseLoginMethodState {
  type: 'chooseLogin',
  email: string,
  hasClassicIdp: boolean,
}

interface ChooseIdpLoginMethodState {
  type: 'chooseIdpLogin',
  email: string,
  showForgotPasswordDialog?: boolean,
}

type LoginPageStatus =
  | EmailEntryState
  | ChooseLoginMethodState
  | ChooseIdpLoginMethodState
  | PasswordEntryState
  | C1TenantResolutionState
  | SSOTenantResolutionState
  | LoggingInState

const DefaultLoginState: EmailEntryState = { type: 'enterEmail' }

export interface LoginPageProps {
  onSuccessfulLogin: (user: User) => void,
}

export function LoginPage({ onSuccessfulLogin }: LoginPageProps): ReactElement {
  const forgeRockURL: string | undefined = useSelector((state: RootState) => state.app.authLocation ?? undefined)
  const { i18n, t } = useTranslation()
  const [loginState, setLoginState] = useState<LoginPageStatus>(DefaultLoginState)
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [errorRequestId, setErrorRequestId] = useState('')
  const [sentForgotPassword, setSentForgotPassword] = useState(false)
  const [forgotPasswordSentTo, setForgotPasswordSentTo] = useState('')
  const enableWebMonitoring = useSelector((state: RootState) => state.app.prerequisites?.enableWebMonitoring ?? false)

  const classes = useStyles()
  function showError(email: string, errorMessage: string, errorRequestId = ''): void {
    setHasError(true)
    setErrorMessage(errorMessage)
    setErrorRequestId(errorRequestId)
    setLoginState({ type: 'enterEmail', lastSubmittedEmail: email })
  }
  function handleCaughtError(email: string, e: unknown): void {
    if (!isAxiosError(e) || !e.response) {
      showError(email, t('defaultError'))
      // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
      console.error('Unhandled error type:', e)
    } else if (!isC1ApiError(e.response.data)) {
      showError(email, e.response.statusText)
    } else {
      showError(email, e.response.data.errorMessage, e.response.data.requestId)
    }
  }
  function handleSelectedLogin(selectedLoginMethod: LoginMethodChoice, hasClassicIdp: boolean, email: string) {
    if (selectedLoginMethod === 'classic') {
      if (hasClassicIdp) {
        setLoginState({ type: 'chooseIdpLogin', email })
      } else {
        setLoginState({ type: 'enterPassword', email })
      }
    } else if (selectedLoginMethod === 'external') {
      if (forgeRockURL) {
        redirectToForgeRockLogin(forgeRockURL, email, enableWebMonitoring)
      } else {
        showError(email, t('defaultError'))
        // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
        console.error('Invalid state achieved. User should not be able to choose external auth when external auth URL has NOT been configured.')
      }
    } else {
      showError(email, t('defaultError'))
      // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
      console.error('Unhandled auth method chosen:', selectedLoginMethod)
    }
  }
  async function attemptGetLoginMethod(email: string): Promise<void> {
    setLoginState({ type: 'loggingIn', email })
    try {
      const { authenticationMethod, hasClassicIdp } = await getLoginMethod(email)
      if (authenticationMethod === 'classic') {
        if (hasClassicIdp) {
          setLoginState({ type: 'chooseIdpLogin', email })
        } else {
          setLoginState({ type: 'enterPassword', email })
        }
      } else if (authenticationMethod === 'external') {
        if (forgeRockURL) {
          redirectToForgeRockLogin(forgeRockURL, email, enableWebMonitoring)
        } else {
          showError(email, t('defaultError'))
          // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
          console.error('Invalid system configuration present. Submitted email is configured for external auth, but the system\'s external auth URL has NOT been configured.')
        }
      } else if (authenticationMethod === 'choice') {
        if (forgeRockURL) {
          setLoginState({ type: 'chooseLogin', email, hasClassicIdp })
        } else {
          showError(email, t('defaultError'))
          // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
          console.error('Invalid system configuration present. Submitted email is configured for auth method choice, but the system\'s external auth URL has NOT been configured and no IDP found for submitted email.')
        }
      } else {
        showError(email, t('defaultError'))
        // eslint-disable-next-line no-console -- TODO: remove this eslint-disable or replace this eslint-disable comment with a proper justification
        console.error('Unhandled auth method:', { authenticationMethod, hasClassicIdp })
      }
    } catch (e) {
      handleCaughtError(email, e)
    }
  }
  async function attemptLogin(email: string, password: string, tenant?: TenantSummary): Promise<void> {
    setLoginState({ type: 'loggingIn', email, tenant })
    try {
      const backendLocaleCode = convertForBackend(i18n.language)
      const resp = await login({ userId: email, password, tenantId: tenant?.id ?? null, locale: backendLocaleCode, selectedLanguage: backendLocaleCode })
      if (resp.type === 'tenantConflict') {
        setLoginState({ type: 'resolveTenant', authMethod: 'c1', email, password, tenants: resp.tenants })
      } else if (resp.type === 'success') {
        onSuccessfulLogin(resp.user)
      }
    } catch (e) {
      handleCaughtError(email, e)
    }
  }
  async function attemptSingleSignOn(email: string, tenant?: TenantSummary): Promise<void> {
    setLoginState({ type: 'loggingIn', email, tenant })
    try {
      const resp = await singleSignOn(email, tenant?.id)
      if (resp.type === 'tenantConflict') {
        setLoginState({ type: 'resolveTenant', authMethod: 'sso', email, tenants: resp.tenants })
      } else if (resp.type === 'success') {
        onSuccessfulLogin(resp.user)
      } else if (resp.type === 'redirect') {
        location.href = resp.redirectTo
        await new Promise(() => { /* Pauses execution to allow the redirect to happen immediately */ })
      }
    } catch (e) {
      handleCaughtError(email, e)
    }
  }
  return <>
    <AuthLayout>
      <Typography variant="h5" className={classes.title}>{t('signInTitle')}</Typography>
      <div className={classes.body}>
        {loginState.type === 'enterEmail' && <EmailEntry
          onNext={email => attemptGetLoginMethod(email)}
          initialValue={loginState.lastSubmittedEmail ?? ''}
        />}
        {loginState.type === 'chooseLogin' && <ChooseLoginMethod
          email={loginState.email}
          onCancel={() => setLoginState({ type: 'enterEmail', lastSubmittedEmail: loginState.email })}
          onSubmit={selectedLoginMethod => handleSelectedLogin(selectedLoginMethod, loginState.hasClassicIdp, loginState.email)}
        />}
        {loginState.type === 'chooseIdpLogin' && <ChooseIdpMethod
          email={loginState.email}
          onCancel={() => setLoginState({ type: 'enterEmail', lastSubmittedEmail: loginState.email })}
          onShowForgotPassword={() => setLoginState({ ...loginState, showForgotPasswordDialog: true })}
          onNext={password => attemptLogin(loginState.email, password)}
          onSingleSignOn={() => attemptSingleSignOn(loginState.email)}
        />}
        {loginState.type === 'enterPassword' && <PasswordEntry
          email={loginState.email}
          onCancel={() => setLoginState({ type: 'enterEmail', lastSubmittedEmail: loginState.email })}
          onShowForgotPassword={() => setLoginState({ ...loginState, showForgotPasswordDialog: true })}
          onSubmit={password => attemptLogin(loginState.email, password)}
        />}
        {(loginState.type === 'enterPassword' || loginState.type === 'chooseIdpLogin') && loginState.showForgotPasswordDialog && <ForgotPasswordDialog
          open={!!loginState.showForgotPasswordDialog}
          initialEmail={loginState.email}
          onCancel={() => setLoginState({ ...loginState, showForgotPasswordDialog: false })}
          onError={(errorMessage, requestId = '') => {
            setHasError(true)
            setErrorMessage(errorMessage)
            setErrorRequestId(requestId)
          }}
          onSuccess={email => {
            setLoginState({ ...loginState, showForgotPasswordDialog: false })
            setSentForgotPassword(true)
            setForgotPasswordSentTo(email)
          }}
        />}
        {loginState.type === 'resolveTenant' && <TenantResolution
          email={loginState.email}
          tenants={loginState.tenants}
          onCancel={() => setLoginState({ type: 'enterEmail', lastSubmittedEmail: loginState.email })}
          onSubmit={tenant => {
            if (loginState.authMethod === 'c1') return attemptLogin(loginState.email, loginState.password, tenant)
            else if (loginState.authMethod === 'sso') return attemptSingleSignOn(loginState.email, tenant)
          }}
        />}
        {loginState.type === 'loggingIn' && <LoggingIn
          email={loginState.email}
          tenant={loginState.tenant}
        />}
      </div>
      <NguiSnackbar
        open={hasError}
        variant="error"
        autoHideDuration={null}
        closeCallback={() => setHasError(false)}
        onClose={() => setHasError(false)}
        data-testid="LoginPage-ErrorSnackbar"
        message={<>
          <div data-testid="LoginPage-ErrorSnackbarMessage">{errorMessage}</div>
          {errorRequestId && <div data-testid="LoginPage-ErrorSnackbarRequestId">{t('requestID')}: {errorRequestId}</div>}
        </>}
      />
      <NguiSnackbar
        open={sentForgotPassword}
        autoHideDuration={3500}
        closeCallback={() => setSentForgotPassword(false)}
        onClose={() => setSentForgotPassword(false)}
        data-testid="LoginPage-SuccessSnackbar"
        message={(
          <span data-testid="LoginPage-SuccessSnackbarMessage">
            {t('passwordResetEmailSentTo', { email: forgotPasswordSentTo })}
          </span>
        )}
      />
    </AuthLayout>
  </>
}
